/**
 * The per-agent table's column catalogue.
 *
 * Lives outside the component because the rules here are the substance of the
 * table, not its markup: which figure a column reads, whether that figure
 * survives a multi-day window, and whether it is fair to colour it against a
 * target. All pure, so they can be tested without mounting a page.
 *
 * Two rules shape the whole thing.
 *
 * Counts first. A rate hides its denominator — 100% is one reply out of one,
 * or nine hundred out of nine hundred, and those deserve different reactions.
 * Every column here is a count you could go and re-count, with the percentage
 * demoted to context beneath it.
 *
 * Colour only where a target exists. The definition sets a first-response
 * target and an overdue ceiling; nothing else has an agreed number. Painting
 * "3 rooms affected" red would invent a company standard, so those columns stay
 * plain — and queue breaches never colour at all, because nobody had claimed
 * the conversation yet, so it is a staffing fact rather than a person's miss.
 */

// Percentiles are nil'd across a multi-day merge: a median of medians is not a
// median, and the daily buckets no longer hold the raw seconds to re-sort. The
// columns that read them are offered only when a single day is selected, and
// hidden from the picker entirely otherwise rather than shown greyed out.
const SINGLE_DAY = true;

const secondsOf = value =>
  value === null || value === undefined ? null : Number(value);

const countOf = value =>
  value === null || value === undefined ? null : Number(value);

const lateCount = row => {
  const eligible = row?.first_human_response?.eligible_count;
  const within = row?.first_human_response?.within_sla_count;
  if (eligible === null || eligible === undefined) return null;
  return Math.max(Number(eligible) - Number(within || 0), 0);
};

// Tone is only meaningful once the sample is big enough to mean anything; below
// that the page shows the count and withholds the judgement.
const rateTone = (rate, target) => {
  if (rate === null || rate === undefined) return null;
  if (rate < target * 0.78) return 'bad';
  if (rate < target) return 'warn';
  return 'good';
};

export const COLUMN_GROUPS = [
  { id: 'identity', labelKey: 'COLGROUP_IDENTITY' },
  { id: 'safety', labelKey: 'COLGROUP_SAFETY' },
  { id: 'frt', labelKey: 'COLGROUP_FRT' },
  { id: 'claim', labelKey: 'COLGROUP_CLAIM' },
  { id: 'rac', labelKey: 'COLGROUP_RAC' },
  { id: 'ongoing', labelKey: 'COLGROUP_ONGOING' },
  { id: 'quality', labelKey: 'COLGROUP_QUALITY' },
  { id: 'actions', labelKey: 'COLGROUP_ACTIONS' },
];

export const COLUMNS = [
  {
    key: 'staff',
    group: 'identity',
    labelKey: 'COL_STAFF',
    kind: 'person',
    locked: true,
  },
  {
    key: 'cases',
    group: 'identity',
    labelKey: 'COL_SAMPLE',
    kind: 'count',
    locked: true,
    value: row => countOf(row?.sample_size),
  },

  {
    key: 'violations',
    group: 'safety',
    labelKey: 'COL_VIOLATIONS',
    kind: 'count',
    defaultOn: true,
    value: row => countOf(row?.safety?.confirmed_violations),
    // The one column that colours on a raw count rather than a rate: saying
    // something unsafe once is not a near-miss.
    tone: row => (Number(row?.safety?.confirmed_violations) > 0 ? 'bad' : null),
  },
  {
    key: 'pendingSafety',
    group: 'safety',
    labelKey: 'COL_PENDING_SAFETY',
    kind: 'count',
    value: row => countOf(row?.safety?.pending_flags),
  },

  {
    key: 'onTime',
    group: 'frt',
    labelKey: 'COL_ON_TIME',
    kind: 'count',
    defaultOn: true,
    value: row => countOf(row?.first_human_response?.within_sla_count),
    ratio: row => row?.first_human_response?.rate,
    tone: (row, ctx) =>
      ctx.scored(row)
        ? rateTone(row?.first_human_response?.rate, ctx.targetRate)
        : null,
  },
  {
    key: 'late',
    group: 'frt',
    labelKey: 'COL_LATE',
    kind: 'count',
    defaultOn: true,
    value: lateCount,
    tone: (row, ctx) => {
      if (!ctx.scored(row)) return null;
      const late = lateCount(row);
      return late > 0
        ? rateTone(row?.first_human_response?.rate, ctx.targetRate)
        : 'good';
    },
  },
  {
    key: 'frtP50',
    group: 'frt',
    labelKey: 'COL_FRT_P50',
    kind: 'seconds',
    singleDayOnly: SINGLE_DAY,
    value: row => secondsOf(row?.first_human_response?.p50),
  },
  {
    key: 'frtP95',
    group: 'frt',
    labelKey: 'COL_FRT_P95',
    kind: 'seconds',
    singleDayOnly: SINGLE_DAY,
    value: row => secondsOf(row?.first_human_response?.p95),
  },
  {
    key: 'frtMax',
    group: 'frt',
    labelKey: 'COL_FRT_MAX',
    kind: 'seconds',
    value: row => secondsOf(row?.first_human_response?.max),
  },

  {
    key: 'claimP50',
    group: 'claim',
    labelKey: 'COL_CLAIM_P50',
    kind: 'seconds',
    singleDayOnly: SINGLE_DAY,
    value: row => secondsOf(row?.claim_latency?.p50),
  },
  {
    key: 'claimP95',
    group: 'claim',
    labelKey: 'COL_CLAIM_P95',
    kind: 'seconds',
    singleDayOnly: SINGLE_DAY,
    value: row => secondsOf(row?.claim_latency?.p95),
  },
  {
    key: 'claimMax',
    group: 'claim',
    labelKey: 'COL_CLAIM_MAX',
    kind: 'seconds',
    value: row => secondsOf(row?.claim_latency?.max),
  },

  {
    key: 'racP50',
    group: 'rac',
    labelKey: 'COL_RAC_P50',
    kind: 'seconds',
    singleDayOnly: SINGLE_DAY,
    value: row => secondsOf(row?.claim_to_reply?.p50),
  },
  {
    key: 'racP95',
    group: 'rac',
    labelKey: 'COL_RAC_P95',
    kind: 'seconds',
    singleDayOnly: SINGLE_DAY,
    value: row => secondsOf(row?.claim_to_reply?.p95),
  },
  {
    key: 'racMax',
    group: 'rac',
    labelKey: 'COL_RAC_MAX',
    kind: 'seconds',
    value: row => secondsOf(row?.claim_to_reply?.max),
  },

  {
    key: 'needReply',
    group: 'ongoing',
    labelKey: 'COL_NEED_REPLY',
    kind: 'count',
    value: row => countOf(row?.ongoing_response?.eligible_turns),
  },
  {
    key: 'overdue',
    group: 'ongoing',
    labelKey: 'COL_OVERDUE',
    kind: 'count',
    defaultOn: true,
    value: row => countOf(row?.ongoing_response?.overdue_turns),
    ratio: row => {
      const eligible = Number(row?.ongoing_response?.eligible_turns);
      if (!Number.isFinite(eligible) || eligible <= 0) return null;
      return Number(row?.ongoing_response?.overdue_turns || 0) / eligible;
    },
    tone: (row, ctx) => {
      const eligible = Number(row?.ongoing_response?.eligible_turns);
      if (!Number.isFinite(eligible) || eligible <= 0) return null;
      const share =
        Number(row?.ongoing_response?.overdue_turns || 0) / eligible;
      if (share > ctx.overdueCeiling * 3) return 'bad';
      if (share > ctx.overdueCeiling) return 'warn';
      return 'good';
    },
  },
  {
    key: 'rooms',
    group: 'ongoing',
    labelKey: 'COL_ROOMS',
    kind: 'count',
    defaultOn: true,
    value: row => countOf(row?.ongoing_response?.overdue_conversations),
  },
  {
    key: 'never',
    group: 'ongoing',
    labelKey: 'COL_NEVER',
    kind: 'count',
    defaultOn: true,
    value: row => countOf(row?.ongoing_response?.final_unanswered),
    // No target to miss: a customer who never got an answer is a miss at one.
    tone: row =>
      Number(row?.ongoing_response?.final_unanswered) > 0 ? 'warn' : null,
  },
  {
    key: 'waitP50',
    group: 'ongoing',
    labelKey: 'COL_WAIT_P50',
    kind: 'seconds',
    value: row => secondsOf(row?.ongoing_response?.p50),
  },
  {
    key: 'waitP95',
    group: 'ongoing',
    labelKey: 'COL_WAIT_P95',
    kind: 'seconds',
    value: row => secondsOf(row?.ongoing_response?.p95),
  },
  {
    key: 'waitMax',
    group: 'ongoing',
    labelKey: 'COL_WAIT_MAX',
    kind: 'seconds',
    value: row => secondsOf(row?.ongoing_response?.max_wait),
  },

  {
    key: 'queueBreach',
    group: 'quality',
    labelKey: 'COL_QUEUE_BREACH',
    kind: 'count',
    // Deliberately tone-less: the breach happened before anyone claimed the
    // conversation, so it describes staffing, not this person's work.
    value: row => countOf(row?.first_human_response?.queue_breach_count),
  },
  {
    key: 'notMeasurable',
    group: 'quality',
    labelKey: 'COL_NOT_MEASURABLE',
    kind: 'count',
    defaultOn: true,
    value: row => countOf(row?.first_human_response?.missing_unknown_count),
  },
  {
    key: 'lang',
    group: 'quality',
    labelKey: 'COL_LANG',
    kind: 'text',
    auto: true,
    value: row => row?.language_hint || null,
  },

  {
    key: 'actions',
    group: 'actions',
    labelKey: 'COL_ACTIONS',
    kind: 'actions',
    locked: true,
  },
];

export const COLUMN_BY_KEY = COLUMNS.reduce((acc, col) => {
  acc[col.key] = col;
  return acc;
}, {});

export const DEFAULT_VISIBLE = COLUMNS.filter(c => c.locked || c.defaultOn).map(
  c => c.key
);

// `lang` appears on its own when some agent has one, and is never a stored
// preference — the account either works in several languages or it does not.
export const isAutoColumn = col => col.auto === true;

export const isPickable = col => !col.locked && !isAutoColumn(col);

/**
 * The columns offered by the picker right now. Percentile columns are dropped
 * from the list entirely on a multi-day window rather than shown disabled: an
 * option you cannot click is a question the reader has to stop and answer.
 */
export const pickableColumns = ({ singleDay }) =>
  COLUMNS.filter(col => isPickable(col) && (!col.singleDayOnly || singleDay));

/**
 * What the table renders, in catalogue order.
 *
 * A stored preference for a percentile column is kept while the window is wide —
 * it simply does not render — so returning to a single day brings the column
 * back without the reader re-ticking it.
 */
export const resolveVisibleColumns = ({ selected, singleDay, hasLanguage }) => {
  const chosen = new Set(selected);
  return COLUMNS.filter(col => {
    if (col.locked) return true;
    if (isAutoColumn(col)) return hasLanguage;
    if (col.singleDayOnly && !singleDay) return false;
    return chosen.has(col.key);
  });
};

export const cellTone = (col, row, ctx) =>
  typeof col.tone === 'function' ? col.tone(row, ctx) || null : null;

export const cellValue = (col, row) =>
  typeof col.value === 'function' ? col.value(row) : null;

export const cellRatio = (col, row) =>
  typeof col.ratio === 'function' ? col.ratio(row) : null;

/**
 * Movement against the same agent in the previous window. Only counts move —
 * a delta on a percentile would compare two numbers that were computed from
 * different populations.
 */
export const cellDelta = (col, row, previousRow) => {
  if (col.kind !== 'count' || !previousRow) return null;
  const now = cellValue(col, row);
  const before = cellValue(col, previousRow);
  if (now === null || before === null) return null;
  return now - before;
};
